import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type TConversationProvider = 'ultravox' | 'heygen' | 'livekit' | 'livekit_ru' | 'liveavatar' | 'yandex';

// Types based on the OpenAPI schema
export interface UserListResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  role_name: string;
  organization_name: string;
}

export interface UserCreateRequest {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  role_name: string;
  organization_id: string;
  organization_name: string;
}

export interface UserUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  role_id?: string;
  role_name?: string;
  organization_id?: string;
}

export interface SubscriptionSettings {
  show_lite_plan: boolean;
  show_advanced_plan: boolean;
  show_enterprise_plan: boolean;
  allow_annual_payment: boolean;
  allow_monthly_payment: boolean;
  default_payment_option: 'monthly' | 'annual';
}

export interface OrganizationBase {
  _id: string;
  name: string;
  trial_available: boolean;
  is_virtual: boolean;
  conversation_provider: TConversationProvider;
  video_recording_enabled: boolean;
  subscription_settings?: SubscriptionSettings;
}

export interface OrganizationDetail extends OrganizationBase {
  subscription_settings: SubscriptionSettings;
}

export interface OrganizationCreateUpdate {
  name: string;
  trial_available: boolean;
  conversation_provider: TConversationProvider;
  video_recording_enabled: boolean;
  subscription_settings: SubscriptionSettings;
}

export interface SubscriptionPeriodSummary {
  id: string;
  subscription_plan_name: string;
  organization_name: string;
  total_excluding_tax: number;
  subtype: string;
  period_end: string;
  period_start: string;
  payment_received: boolean;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodCreate {
  subscription_plan_id: string;
  subscription_plan_name: string;
  organization_id: string;
  organization_name: string;
  total_excluding_tax: number;
  period_start: string;
  period_end: string;
  payment_received: boolean;
  type: string;
  purpose_type: string;
  project_limit: number;
  session_limit: number;
}

export interface SubscriptionPeriodUpdate {
  subscription_plan_id?: string;
  subscription_plan_name?: string;
  total_excluding_tax?: number;
  period_start?: string;
  period_end?: string;
  payment_received: boolean;
  type?: string;
  purpose_type?: string;
  project_limit?: number;
  session_limit?: number;
}

export interface SelectBase {
  _id: string;
  name: string;
}

export type IdeaAccessibility = 'public' | 'private';
export type IdeaOrder = 'UPDATED_DESC' | 'CREATED_DESC' | 'CREATED_ASC' | 'TITLE_ASC' | 'RANDOM';
export type IdeaStatusId = 1 | 2 | 3 | 4 | 5;
export type IdeaCategoryId =
  | 'scenario_creation'
  | 'scenario_access'
  | 'roleplay_passing'
  | 'results_and_reports'
  | 'other';

export interface IdeaRecord {
  id: string;
  title: string;
  description: string;
  dev_comment: string | null;
  accessibility_id: IdeaAccessibility;
  note: string | null;
  costs: number | null;
  status_id: IdeaStatusId;
  category_id: IdeaCategoryId | null;
  organization_id: string;
  user_id: string;
  i_have_vote: boolean | null;
  votes_count: number | null;
  criticality_sum: number | null;
  organization_votes_count: number | null;
  avg_criticality: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface IdeasQueryParams {
  search?: string;
  status_id?: IdeaStatusId;
  category_id?: IdeaCategoryId;
  organization_id?: string;
  user_id?: string;
  accessibility_id?: IdeaAccessibility;
  order?: IdeaOrder;
  display_mode?: 'ALL_IDEAS' | 'ONLY_MY_IDEAS';
  page?: number;
  limit?: number;
}

export interface IdeasListResponse {
  items: IdeaRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface IdeaUpdateRequest {
  title?: string;
  description?: string;
  dev_comment?: string | null;
  accessibility_id?: IdeaAccessibility;
  note?: string | null;
  costs?: number | null;
  status_id?: IdeaStatusId;
  category_id?: IdeaCategoryId | null;
}

export interface IdeaVoteAnswer {
  id: 'importance_1' | 'importance_2' | 'importance_3';
  importance: number;
  value: string;
}

export interface IdeaVoteRecord {
  id: string;
  idea_id: string;
  answer_id: 'importance_1' | 'importance_2' | 'importance_3';
  description: string;
  organization_id: string;
  user_id: string;
  created_at: string;
  updated_at: string | null;
  answer: IdeaVoteAnswer | null;
}

export interface IdeaVotesListResponse {
  items: IdeaVoteRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface TariffUsageSummary {
  id: string;
  organization_id: string;
  organization_name: string;
  purpose_type: string;
  period_start: number;
  period_end: number;
  session_limit: number | null;
  sessions_used: number;
}

export interface ActivityUsageOrganizationRow {
  organization_id: string;
  organization_name: string;
  sessions_by_date: Record<string, number>;
  total_sessions: number;
}

export interface ActivityUsageResponse {
  purpose_type: string;
  dates: string[];
  organizations: ActivityUsageOrganizationRow[];
}

// Create the API client
export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_APP_API_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem('auth_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Users', 'Organizations', 'SubscriptionPeriods', 'Selects', 'Ideas'],
  endpoints: (builder) => ({
    // Users
    getUsers: builder.query<UserListResponse[], void>({
      query: () => '/users/',
      providesTags: ['Users'],
    }),
    getUser: builder.query<UserListResponse, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createUser: builder.mutation<void, UserCreateRequest>({
      query: (body) => ({
        url: '/users/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: builder.mutation<void, { id: string; body: UserUpdateRequest }>({
      query: ({ id, body }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Users', id }, {type: 'Users'}],
    }),

    // Organizations
    getOrganizations: builder.query<OrganizationBase[], void>({
      query: () => '/organizations/',
      providesTags: ['Organizations'],
    }),
    getOrganization: builder.query<OrganizationDetail, string>({
      query: (id) => `/organizations/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Organizations', id }],
    }),
    createOrganization: builder.mutation<void, OrganizationCreateUpdate>({
      query: (body) => ({
        url: '/organizations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organizations'],
    }),
    updateOrganization: builder.mutation<void, { id: string; body: OrganizationCreateUpdate }>({
      query: ({ id, body }) => ({
        url: `/organizations/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Organizations', id }, { type: 'Organizations' }],
    }),

    // Subscription Periods
    getSubscriptionPeriods: builder.query<SubscriptionPeriodSummary[], void>({
      query: () => '/subscription_periods/',
      providesTags: ['SubscriptionPeriods'],
    }),
    getSubscriptionPeriod: builder.query<SubscriptionPeriodSummary, string>({
      query: (id) => `/subscription_periods/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'SubscriptionPeriods', id }],
    }),
    createSubscriptionPeriod: builder.mutation<void, SubscriptionPeriodCreate>({
      query: (body) => ({
        url: '/subscription_periods/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),
    updateSubscriptionPeriod: builder.mutation<void, { id: string; body: SubscriptionPeriodUpdate }>({
      query: ({ id, body }) => ({
        url: `/subscription_periods/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'SubscriptionPeriods', id }, { type: 'SubscriptionPeriods' }],
    }),
    deleteSubscriptionPeriod: builder.mutation<void, string>({
      query: (id) => ({
        url: `/subscription_periods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubscriptionPeriods'],
    }),
    getTariffUsage: builder.query<TariffUsageSummary[], void>({
      query: () => '/admin/tariff-usage',
    }),
    getActivityUsage: builder.query<ActivityUsageResponse, 'paid' | 'trial'>({
      query: (purposeType) => `/admin/activity-usage/${purposeType}`,
    }),

    // Ideas
    getIdeas: builder.query<IdeasListResponse, IdeasQueryParams | void>({
      query: (params) => ({
        url: '/ideas/',
        params: params ?? undefined,
      }),
      transformResponse: (response: IdeaRecord[], meta): IdeasListResponse => ({
        items: response,
        total: Number(meta?.response?.headers.get('X-Total-Count') ?? response.length),
        page: Number(meta?.response?.headers.get('X-Page') ?? 1),
        limit: Number(meta?.response?.headers.get('X-Limit') ?? response.length),
        hasMore: (meta?.response?.headers.get('X-Has-More') ?? 'false') === 'true',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Ideas' as const, id })),
              { type: 'Ideas' as const, id: 'LIST' },
            ]
          : [{ type: 'Ideas' as const, id: 'LIST' }],
    }),
    getIdea: builder.query<IdeaRecord, string>({
      query: (id) => `/ideas/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Ideas', id }],
    }),
    getIdeaVotes: builder.query<IdeaVotesListResponse, { ideaId: string; page?: number; limit?: number }>({
      query: ({ ideaId, page = 1, limit = 20 }) => ({
        url: `/ideas/${ideaId}/votes`,
        params: { page, limit },
      }),
      transformResponse: (response: IdeaVoteRecord[], meta): IdeaVotesListResponse => ({
        items: response,
        total: Number(meta?.response?.headers.get('X-Total-Count') ?? response.length),
        page: Number(meta?.response?.headers.get('X-Page') ?? 1),
        limit: Number(meta?.response?.headers.get('X-Limit') ?? response.length),
        hasMore: (meta?.response?.headers.get('X-Has-More') ?? 'false') === 'true',
      }),
    }),
    updateIdea: builder.mutation<{ id: string }, { id: string; body: IdeaUpdateRequest }>({
      query: ({ id, body }) => ({
        url: `/ideas/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Ideas', id }, { type: 'Ideas', id: 'LIST' }],
    }),
    deleteIdea: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/ideas/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Ideas', id }, { type: 'Ideas', id: 'LIST' }],
    }),

    // Selects
    getOrganizationsSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/organizations',
      providesTags: ['Selects'],
    }),
    getRolesSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/roles',
      providesTags: ['Selects'],
    }),
    getSubscriptionPlansSelect: builder.query<SelectBase[], void>({
      query: () => '/selects/subscription_plans',
      providesTags: ['Selects'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetSubscriptionPeriodsQuery,
  useGetSubscriptionPeriodQuery,
  useCreateSubscriptionPeriodMutation,
  useUpdateSubscriptionPeriodMutation,
  useDeleteSubscriptionPeriodMutation,
  useGetTariffUsageQuery,
  useGetActivityUsageQuery,
  useGetIdeasQuery,
  useGetIdeaQuery,
  useGetIdeaVotesQuery,
  useUpdateIdeaMutation,
  useDeleteIdeaMutation,
  useGetOrganizationsSelectQuery,
  useGetRolesSelectQuery,
  useGetSubscriptionPlansSelectQuery,
} = api;
